import type { Delivery, IDeliveryLine } from '../interfaces/ILogistics.js';
import { orderLinesFor } from './lines.js';
import { sameUnit } from '../../logistics/index.js';
import { fold } from '../utils/text.js';

/** What a material's most recent arrival brought, for prefilling a stock entry. */
export type DeliveredAmount = {
  quantity: number;
  unit: string | null;
  /** The order it came from, so the UI can say where the number is from. */
  ref: string;
};

/**
 * Material name (folded) → what its latest arrival delivered.
 *
 * Keyed by the same NFKC+lowercase fold every other delivery↔material link on
 * this page uses, so 'ﾃｽﾄ資材' and 'テスト資材' resolve to one material.
 *
 * Only an order that has actually landed describes material on site, and the
 * arrival stamp is the sole arrival fact — a pending request is a promise, and
 * prefilling a stock count from one would record inventory nobody has taken
 * delivery of.
 *
 * Lines with no recorded quantity are skipped entirely rather than contributing
 * a zero — Genba supplies no amount, so most historical rows have none, and a
 * fabricated 0 would prefill a stock entry claiming nothing arrived.
 *
 * A truck carrying three materials contributes one entry per LINE: each
 * material's own amount, never the truck's total.
 */
export function buildDeliveredAmounts(deliveries: Delivery[]): Record<string, DeliveredAmount> {
  const out: Record<string, DeliveredAmount> = {};
  // Latest wins. `deliveredAt` is the arrival instant, present by construction
  // on every row the filter lets through.
  const arrived = deliveries
    .filter((d) => d.deliveredAt != null)
    .sort((a, b) => (a.deliveredAt ?? '').localeCompare(b.deliveredAt ?? ''));

  for (const d of arrived) {
    for (const line of d.lines) {
      if (line.quantity == null) continue;
      out[fold(line.itemName)] = { quantity: line.quantity, unit: line.unit, ref: d.ref };
    }
  }
  return out;
}

/** Everything a material took delivery of since its current count was entered —
 *  the material actually sitting on top of the running stock. */
export type ArrivedSince = {
  /**
   * Summed across every arrival since the count — two deliveries landing in one
   * week are both physically on site, so merging only the newest would lose one.
   *
   * Null when ANY of those arrivals recorded no amount, and ALSO when they
   * recorded amounts in different units. A partial sum would be a confident
   * understatement, and a cross-unit sum is worse — 10 sheets + 5 bundles is
   * not 15 of anything, yet it used to come back as exactly that and land in
   * the dialog as a locked total beside the stock's own unit. Null makes the
   * caller ask for the merged figure instead.
   */
  quantity: number | null;
  /** Null when the arrivals disagree on unit (or record none) — the caller must
   *  not silently add sheets to bundles. */
  unit: string | null;
  /** True when the arrivals recorded amounts in more than one unit — the one
   *  null-quantity case that is a disagreement rather than missing data, so the
   *  dialog can explain it instead of claiming nothing was recorded. */
  mixedUnits: boolean;
  refs: string[];
};

/** What a stock dialog is handed about the arrival it reconciles — an
 *  `ArrivedSince`, where a caller that never mixes units may leave out
 *  `mixedUnits`. */
export type IArrivedFigures = Omit<ArrivedSince, 'mixedUnits'> & Partial<Pick<ArrivedSince, 'mixedUnits'>>;

/**
 * Folds one more arrival — an order and the LINE of it that carried this
 * material — into an `ArrivedSince` accumulator, on the rules
 * `arrivedSince` folds a whole delivery list by: amounts add only while every
 * arrival recorded one AND they all name the same unit, because 10 sheets +
 * 5 bundles is not 15 of anything. `mixedUnits` is sticky — once one pair of
 * arrivals has disagreed, a later matching unit doesn't undo it.
 *
 * A ref the accumulator already counted is left alone — folding in a
 * delivery that has already landed in `since` would double the amount the
 * dialog says turned up. This is what lets `buildDeliveredStockDialog` fold
 * a just-arrived delivery into a freshly computed `arrivedSince` without
 * knowing whether the page's cached list already includes it.
 */
export function withArrival(since: ArrivedSince | null, d: Delivery, line: IDeliveryLine): ArrivedSince {
  if (!since) return { quantity: line.quantity, unit: line.unit, mixedUnits: false, refs: [d.ref] };
  if (since.refs.includes(d.ref)) return since;
  // Compared as UNITS: 'sheets' and '枚' are one unit, whoever typed which.
  const mixedUnits = since.mixedUnits || !sameUnit(since.unit, line.unit);
  const complete = since.quantity != null && line.quantity != null;
  return {
    quantity: complete && !mixedUnits ? since.quantity! + line.quantity! : null,
    unit: mixedUnits ? null : (since.unit ?? line.unit ?? null),
    mixedUnits,
    refs: [...since.refs, d.ref],
  };
}

/**
 * What landed for `materialName` strictly after `enteredAtIso`.
 *
 * This is the Sync counterpart to `buildDeliveredAmounts`: that one answers
 * "what is this material's latest arrival" for a fresh stock entry, this one
 * answers "what has arrived on top of the count I already have".
 *
 * Returns null only when nothing arrived at all. An arrival with no recorded
 * quantity still counts — it is why the row is prompting — so it comes back with
 * its ref and a null quantity rather than being dropped. Dropping it was a real
 * dead end: the row offered Sync (the backend flags the arrival from
 * `deliveredAt` alone) while the dialog saw nothing to merge, degraded to the
 * plain form, and failed the save with "sync requires remaining".
 *
 * Reads the LINE for the material on each arrived order, so a truck that also
 * carried two other materials contributes only this one's amount.
 *
 * A one-step fold over `withArrival`, in delivery order — the accumulator
 * carries the same sum/mixed-unit rules a single arrival folds by, so there
 * is exactly one place that logic lives.
 */
export function arrivedSince(
  deliveries: Delivery[],
  materialName: string,
  enteredAtIso: string,
): ArrivedSince | null {
  const since = orderLinesFor(deliveries, materialName).filter(
    ({ order }) =>
      order.deliveredAt != null && Date.parse(order.deliveredAt) > Date.parse(enteredAtIso),
  );
  if (since.length === 0) return null;

  return since.reduce<ArrivedSince | null>((acc, { order, line }) => withArrival(acc, order, line), null);
}
